import { create } from 'zustand';
import { BusiestTimeService, type BusiestTimeRecord, type DateBasedTimeSlotData, type DateLabelPair } from '../lib/services/analytics/busiestTimeService';
import { useAuthStore } from './authStore';
import { busiestTimeDateRangePresets } from '../lib/services/analytics/dateRangePresets';

type GroupingType = 'weekday' | 'day' | 'month';

interface BusiestTimeState {
  selectedTimezone: string;
  timeData: DateBasedTimeSlotData[];
  dateLabels: DateLabelPair[];
  grouping: GroupingType;
  rawData: BusiestTimeRecord[];
  availableTimezones: string[];
  isLoading: boolean;
  error: string | null;
  dateRange: { startTime: string; endTime: string };
  selectedPeriod: string;
  setTimezone: (timezone: string) => void;
  setTimeData: (data: DateBasedTimeSlotData[], dateLabels: DateLabelPair[], grouping: GroupingType) => void;
  setRawData: (data: BusiestTimeRecord[]) => void;
  setAvailableTimezones: (timezones: string[]) => void;
  setLoading: (loading: boolean) => void;
  setDateRange: (range: { startTime: string; endTime: string }) => void;
  setSelectedPeriod: (period: string) => void;
  fetchTimeData: () => Promise<void>;
  reset: () => void;
}

export const useBusiestTimeStore = create<BusiestTimeState>((set, get) => ({
  selectedTimezone: 'all',
  timeData: [],
  dateLabels: [],
  grouping: 'weekday',
  rawData: [],
  availableTimezones: [],
  isLoading: false,
  error: null,
  dateRange: busiestTimeDateRangePresets().last7Days,
  selectedPeriod: 'last7Days',

  setTimezone: timezone => {
    const state = get();
    set({ selectedTimezone: timezone });

    // Filter and transform data based on new timezone
    if (state.rawData.length > 0) {
      const filteredData = timezone === 'all'
        ? state.rawData
        : state.rawData.filter(record => record.timezone === timezone);

      const { data, dateLabels, grouping } = BusiestTimeService.transformToDateBasedData(
        filteredData,
        state.dateRange.startTime,
        state.dateRange.endTime
      );
      set({ timeData: data, dateLabels, grouping });
    }
  },

  setTimeData: (data, dateLabels, grouping) => set({ timeData: data, dateLabels, grouping }),

  setRawData: data => set({ rawData: data }),

  setAvailableTimezones: timezones => set({ availableTimezones: timezones }),

  setLoading: loading => set({ isLoading: loading }),

  setDateRange: range => {
    set({ dateRange: range });
    // Refetch data when date range changes
    get().fetchTimeData();
  },

  setSelectedPeriod: period => {
    set({ selectedPeriod: period });
    const presets = busiestTimeDateRangePresets();
    let dateRange: { startTime: string; endTime: string } | null = null;

    if (period === 'last7Days') {
      dateRange = presets.last7Days;
    } else if (period === 'last30Days') {
      dateRange = presets.last30Days;
    } else if (period === 'last3Months') {
      dateRange = presets.last3Months;
    } else if (period === 'today') {
      const today = new Date().toISOString().split('T')[0];
      dateRange = { startTime: today, endTime: today };
    } else if (period === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      dateRange = { startTime: yesterdayStr, endTime: yesterdayStr };
    } else if (period === 'last12Months') {
      const today = new Date();
      const endTime = today.toISOString().split('T')[0];
      const startTime = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
      dateRange = { startTime, endTime };
    }

    if (dateRange) {
      set({ dateRange });
      get().fetchTimeData();
    }
  },

  fetchTimeData: async () => {
    const state = get();

    // Prevent duplicate requests if already loading
    if (state.isLoading) {
      return;
    }

    const { user } = useAuthStore.getState();
    if (!user?.id) {
      set({ error: 'User not authenticated' });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const { dateRange, selectedTimezone } = get();

      // Fetch raw data first
      const rawData = await BusiestTimeService.getRawBusiestTimes({
        startTime: dateRange.startTime,
        endTime: dateRange.endTime,
        userId: user.id,
      });

      // Extract available timezones
      const availableTimezones = BusiestTimeService.extractAvailableTimezones(rawData);

      // Check if selected timezone is still valid, reset to 'all' if not
      let currentTimezone = selectedTimezone;
      if (currentTimezone !== 'all' && !availableTimezones.includes(currentTimezone)) {
        currentTimezone = 'all';
      }

      // Filter data based on current timezone
      const filteredData = currentTimezone === 'all'
        ? rawData
        : rawData.filter(record => record.timezone === currentTimezone);

      // Transform filtered data with date-based grouping
      const { data: timeData, dateLabels, grouping } = BusiestTimeService.transformToDateBasedData(
        filteredData,
        dateRange.startTime,
        dateRange.endTime
      );

      set({
        rawData,
        availableTimezones,
        selectedTimezone: currentTimezone,
        timeData,
        dateLabels,
        grouping,
      });
    } catch (error) {
      console.error('Failed to fetch busiest time data:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to load busiest time data' });
    } finally {
      set({ isLoading: false });
    }
  },

  reset: () => {
    set({
      selectedTimezone: 'all',
      timeData: [],
      dateLabels: [],
      grouping: 'weekday',
      rawData: [],
      availableTimezones: [],
      isLoading: false,
      error: null,
      dateRange: busiestTimeDateRangePresets().last7Days,
      selectedPeriod: 'last7Days',
    });
  },
}));
